/**
 * @file React hook — useA1Prefill (state and side effects).
 */

import { FormSubSection } from "../../../../../../interfaces/formInterfaces";
import { formatEmbodiedCarbonReferenceQtyFromUnknown, formatEmbodiedCarbonFunctionalUnitFromUnknown } from "../../shared/embodiedCarbonReferenceQtyFormat";

/** Row fields that must show Background raw material (id + label) even if type ≠ RAW_MATERIALS in CMS. */
export const A1_RAW_MATERIAL_ROW_FIELD_NAMES = new Set([
  "rawMaterial_extraction",
  "rawMaterial_extraction_details",
  "rawMaterialsExtractionProcesses",
  "rawMaterialsEmbodiedCarbonDetails",
]);

type ValuesSetter = (
  updater: (v: Record<string, string>) => Record<string, string>
) => void;

type RowsSetter = (
  updater: (s: Record<string, number>) => Record<string, number>
) => void;

/** Hook: use A1Prefill. */
export function useA1Prefill() {
  const prefillFromSubmission = (
    subSections: FormSubSection[],
    submission: any,
    normalizedRaw: any[],
    setValues: ValuesSetter,
    setSectionRows: RowsSetter,
    /** When set, only accept saved A1 rawMaterialId if it is a catalog `raw_materials.id`. */
    validCatalogRawMaterialIds?: Set<number> | null
  ) => {
    try {
      const nextValues: Record<string, string> = {};
      const nextRows: Record<string, number> = {};

      const sectionToApiMap: Record<string, string> = {
        A1_RAW_EXTRACTION: "A1_Extraction",
        A1_FUEL_EXTRACTION: "A1_Fuel",
        A1_EMBODIED_ENERGY: "A1_Energy",
        A1_EMBODIED_CARBON: "A1_Carbon",
      };

      subSections.forEach((sec) => {
        const code = sec.code;
        if (!code) return;

        const apiKey = sectionToApiMap[code];
        const apiRows: any[] = Array.isArray(submission?.[apiKey])
          ? submission[apiKey]
          : [];

        // ✅ ROW COUNT SOURCE OF TRUTH
        const rows =
          apiRows.length > 0
            ? apiRows.length
            : Math.max(normalizedRaw.length, 1);

        nextRows[code] = rows;

        for (let i = 0; i < rows; i++) {
          const rowKey = `row${i}`;
          const item = apiRows[i];

          sec.fields.forEach((f) => {
            const inputName = `${f.name}_${code}_${rowKey}`;

            const isRawMaterialRowField =
              f.type === "RAW_MATERIALS" ||
              A1_RAW_MATERIAL_ROW_FIELD_NAMES.has(f.name);

            if (isRawMaterialRowField) {
              const nm = normalizedRaw[i];
              const fromItem =
                item?.rawMaterialId ?? item?.raw_material_id;
              let rid: string | number = "";
              if (nm?.catalogId != null && nm.catalogId > 0) {
                rid = nm.catalogId;
              } else if (
                fromItem != null &&
                String(fromItem).trim() !== ""
              ) {
                const n = Number(fromItem);
                const allowSaved =
                  !validCatalogRawMaterialIds ||
                  validCatalogRawMaterialIds.size === 0 ||
                  (Number.isFinite(n) && validCatalogRawMaterialIds.has(n));
                if (allowSaved) rid = fromItem;
              } else if (nm?.id != null && nm.id > 0) {
                rid = nm.id;
              }
              nextValues[inputName] =
                rid !== "" && rid != null ? String(rid) : "";
              nextValues[`${inputName}_label`] =
                item?.rawMaterialName ??
                item?.raw_material_name ??
                normalizedRaw[i]?.name ??
                "";
              return;
            }

            if (!item) {
              nextValues[inputName] = "";
              return;
            }

            // EXTRACTION
            if (f.name === "extractionLatitude")
              nextValues[inputName] = item.latitude ?? "";
            else if (f.name === "extractionLongitude")
              nextValues[inputName] = item.longitude ?? "";
            else if (f.name === "extractionState")
              nextValues[inputName] = item.state ?? "";
            else if (f.name === "extractionDistrict")
              nextValues[inputName] = item.district ?? "";
            else if (f.name === "extractionPincode")
              nextValues[inputName] = item.pincode ?? "";
            else if (f.name === "extractionStatus")
              nextValues[inputName] = item.extractionStatus ?? "";
            else if (
              f.type === "EXTRACTION_PROCESS" ||
              f.name === "RawMaterialExtractionProcesses"
            ) {
              const ep =
                item.extractionProcess ?? item.extractionProcessId ?? "";
              nextValues[inputName] =
                ep !== null && ep !== undefined && ep !== ""
                  ? String(ep)
                  : "";
            }

            // FUEL
            else if (f.name === "fuelMix_extraction")
              nextValues[inputName] = item.fuelMix ?? "";
            else if (f.name === "fuelQuantity_extraction")
              nextValues[inputName] = item.quantity ?? "";
            else if (f.name === "fuelQuantity_unit_extraction")
              nextValues[inputName] =
                item.unitId != null ? String(item.unitId) : "";
            else if (f.name === "fuelDensity_extraction")
              nextValues[inputName] = item.fuelDensity ?? "";
            else if (f.name === "fuelDensity_unit_extraction")
              nextValues[inputName] =
                item.fuelDensityUnit != null
                  ? String(item.fuelDensityUnit)
                  : "";
            else if (f.name === "calorificValue_extraction")
              nextValues[inputName] = item.calorificValue ?? "";
            else if (f.name === "calorificValue_unit_extraction")
              nextValues[inputName] =
                item.calorificValueUnit != null
                  ? String(item.calorificValueUnit)
                  : "";

            // ENERGY
            else if (f.name === "embodiedEnergy_extraction")
              nextValues[inputName] = item.embodiedEnergy ?? "";
            else if (f.name === "embodiedEnergy_unit_extraction")
              nextValues[inputName] =
                item.unitId != null ? String(item.unitId) : "MJ";

            // CARBON
            else if (f.name === "characterizationFactor_extraction")
              nextValues[inputName] =
                item.characterizationFactor ?? "";
            else if (f.name === "characterizationFactor_unit_extraction")
              nextValues[inputName] =
                item.characterizationFactorUnit != null
                  ? String(item.characterizationFactorUnit)
                  : "";
            else if (f.name === "embodiedCarbon_referenceQty_extraction")
              nextValues[inputName] = formatEmbodiedCarbonReferenceQtyFromUnknown(
                item.referenceQty ?? ""
              );
            else if (f.name === "embodiedCarbon_referenceQty_unit_extraction")
              nextValues[inputName] =
                item.referenceQtyUnitId != null
                  ? String(item.referenceQtyUnitId)
                  : "";
            else if (f.name === "embodiedCarbon_functionalUnit_extraction")
              nextValues[inputName] = formatEmbodiedCarbonFunctionalUnitFromUnknown(
                item.functionalUnitCarbon ?? ""
              );
            else if (
              f.name ===
              "embodiedCarbon_functionalUnit_unit_extraction"
            )
              nextValues[inputName] =
                item.functionalUnitCarbonUnitId != null
                  ? String(item.functionalUnitCarbonUnitId)
                  : "";
            else nextValues[inputName] = "";
          });
        }
      });

      setSectionRows((p) => ({ ...p, ...nextRows }));
      setValues((p) => ({ ...p, ...nextValues }));
    } catch (err) {
      console.error("❌ A1 Prefill failed:", err);
    }
  };

  return { prefillFromSubmission };
}